import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Music,
  ListMusic,
  FileText,
  Settings,
  LogOut,
  ClipboardList,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

interface AdminLayoutProps {
  children: React.ReactNode;
}

const navigation = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { name: "Artists", href: "/artists", icon: Music },
  { name: "Songs", href: "/songs", icon: ListMusic },
  { name: "Tabs", href: "/tabs", icon: FileText },
  { name: "Tab Submissions", href: "/tab-submissions", icon: ClipboardList },
  { name: "Settings", href: "/settings", icon: Settings },
];

export function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Check if we're on localhost
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "[::1]");

  // Check if URL contains "stg"
  const isStaging =
    typeof window !== "undefined" &&
    window.location.href.toLowerCase().includes("stg");

  // Determine which label to show
  const showLabel = isLocalhost || isStaging;
  const labelText = isStaging ? "STAGING" : "DEV";

  return (
    <div className="min-h-screen flex bg-background relative">
      {/* DEV/STAGING Label */}
      {showLabel && (
        <div
          className="fixed left-0 top-1/2 z-50"
          style={{
            writingMode: "vertical-rl",
            textOrientation: "mixed",
            transform: "translateY(-50%) rotate(180deg)",
            backgroundColor: "#dc2626", // Red background
            color: "white",
            padding: "12px 0",
            fontSize: "10px",
            fontWeight: "bold",
            letterSpacing: "2px",
            boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
            borderRadius: "4px 0 0 4px",
          }}
        >
          {labelText}
        </div>
      )}
      {/* Sidebar */}
      <aside className="w-64 border-r border-border bg-card flex flex-col">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-bold">Admin Panel</h2>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;

            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <Icon className="h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-medium text-foreground">{user?.email}</p>
            <p className="text-xs text-muted-foreground capitalize">
              {user?.role}
            </p>
          </div>
          <Separator className="my-2" />
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="container mx-auto p-8">{children}</div>
      </main>
    </div>
  );
}
